import { useState, useEffect } from 'react';
import { GradeData } from '../types';
import { getGrilleData, validateGrilleData } from '../services/grilleHopitalService';
import { GRADES_IDE } from '../constants/salary';
import { Profession } from '../types';

/**
 * @fileoverview Hook for managing grille data from Supabase for any profession
 * 
 * This hook provides a clean interface for components to access grille data
 * with proper loading states, error handling, and fallback to hardcoded data.
 * 
 * @author App'Ines Team
 * @version 1.0.0
 * @since 2025
 */

interface UseGrilleDataResult {
  grades: GradeData[];
  isLoading: boolean;
  error: string | null;
  isUsingFallback: boolean;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for fetching and managing grille data for any profession
 * 
 * This hook handles the complete lifecycle of grille data:
 * - Fetches data from Supabase on mount
 * - Provides loading and error states
 * - Falls back to hardcoded data if Supabase fails
 * - Validates data integrity
 * - Provides a refetch function for manual refresh
 * 
 * @param {Profession} selectedProfession - The profession object containing supabaseTable
 * @returns {UseGrilleDataResult} Object containing grades data and state
 * 
 * @example
 * function MyComponent() {
 *   const { grades, isLoading, error, isUsingFallback } = useGrilleData(selectedProfession);
 *   
 *   if (isLoading) return <LoadingSpinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *   
 *   return <GradeSelector grades={grades} />;
 * }
 */
export function useGrilleData(selectedProfession: Profession): UseGrilleDataResult {
  const [grades, setGrades] = useState<GradeData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUsingFallback, setIsUsingFallback] = useState(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setIsUsingFallback(false);

      console.log(`🔍 useGrilleData - Fetching ${selectedProfession.supabaseTable} data from Supabase...`);
      console.log('🔍 useGrilleData - selectedProfession:', selectedProfession);
      console.log('🔍 useGrilleData - Table name:', selectedProfession.supabaseTable);
      
      const supabaseGrades = await getGrilleData(selectedProfession.supabaseTable);
      
      console.log('🔍 useGrilleData - Received supabaseGrades:', supabaseGrades);
      console.log('🔍 useGrilleData - supabaseGrades length:', supabaseGrades?.length || 0);
      console.log('🔍 useGrilleData - All grades summary:', supabaseGrades?.map(g => ({
        id: g.id,
        nom: g.nom,
        echelonsCount: g.echelons.length,
        echelonRange: g.echelons.length > 0 ? `${Math.min(...g.echelons.map(e => e.echelon))} to ${Math.max(...g.echelons.map(e => e.echelon))}` : 'no echelons'
      })));
      console.log('🔍 useGrilleData - Grade 3 details (if exists):', supabaseGrades?.find(g => g.id === 3));
      
      // Validate the fetched data
      if (!validateGrilleData(supabaseGrades)) {
        console.warn('🔍 useGrilleData - Invalid grille data structure received from Supabase. Falling back to hardcoded data.');
        // Only use GRADES_IDE fallback for IDE profession, empty array for others
        const fallbackGrades = selectedProfession.supabaseTable === 'grille_Hopital_IDE' ? GRADES_IDE : [];
        console.log('🔍 useGrilleData - Using fallback grades:', fallbackGrades.length, 'grades for table:', selectedProfession.supabaseTable);
        setGrades(fallbackGrades);
        setIsUsingFallback(true);
        setError('Invalid grille data structure received from Supabase. Using fallback data.');
        setIsLoading(false);
        return;
      }

      setGrades(supabaseGrades);
      console.log('🔍 useGrilleData - Successfully loaded grille data from Supabase');
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('🔍 useGrilleData - Failed to fetch grille data from Supabase:', errorMessage);
      
      // Fallback to hardcoded data
      console.log('🔍 useGrilleData - Falling back to hardcoded grille data');
      // Only use GRADES_IDE fallback for IDE profession, empty array for others
      const fallbackGrades = selectedProfession.supabaseTable === 'grille_Hopital_IDE' ? GRADES_IDE : [];
      console.log('🔍 useGrilleData - Using fallback grades:', fallbackGrades.length, 'grades for table:', selectedProfession.supabaseTable);
      setGrades(fallbackGrades);
      setIsUsingFallback(true);
      setError(`Supabase connection failed: ${errorMessage}. Using fallback data.`);
      
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [selectedProfession.supabaseTable]);

  const refetch = async () => {
    await fetchData();
  };

  return {
    grades,
    isLoading,
    error,
    isUsingFallback,
    refetch
  };
}